import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, Loader2, Pencil, Plus, Save, ShieldCheck, Trash2, Upload } from 'lucide-react';
import I18nTripleFields, { I18nTriple } from '../../components/Admin/I18nTripleFields';
import { useConfirm } from '../../contexts/ConfirmContext';
import Breadcrumbs from '../../components/Admin/Breadcrumbs';
import { Button, IconButton, Tabs } from '../../components/UI';
import { useNotifications } from '../../hooks/useNotifications';
import {
  AdminChecklist,
  ChecklistStatus,
  ChecklistTemplate,
  Lang,
  TemplateItem,
  VISA_TYPES,
  VisaType,
  errorMessage,
} from '../../components/DocumentChecklist/documentChecklist';

const STATUS_FILTERS: { value: '' | ChecklistStatus; label: string }[] = [
  { value: 'submitted', label: 'Para revisar' },
  { value: 'awaiting_client', label: 'Aguardando cliente' },
  { value: 'completed', label: 'Concluídos' },
  { value: '', label: 'Todos' },
];

const STATUS_LABELS: Record<ChecklistStatus, string> = {
  awaiting_client: 'Aguardando cliente',
  submitted: 'Enviado — revisar',
  completed: 'Concluído',
  revoked: 'Revogado',
  expired: 'Expirado',
};

const AdminDocumentos: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab = tabParam === 'modelos' || tabParam === 'termo' ? tabParam : 'pedidos';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <div className="mb-4">
        <Breadcrumbs items={[{ label: t('breadcrumbs.dashboard'), href: '/admin' }, { label: t('admin.documentos') }]} />
      </div>
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">{t('admin.documentos')}</h1>
      <p className="text-sm text-gray-600 mb-6">
        Os pedidos são criados na página de cada cliente (CRM → cliente → Documentos). Aqui ficam a fila de revisão e
        os modelos de checklist por tipo de visto.
      </p>

      <Tabs
        className="mb-6"
        ariaLabel={t('admin.documentos')}
        value={tab}
        onChange={(key) => setSearchParams(key === 'pedidos' ? {} : { tab: key })}
        items={[
          { id: 'pedidos', label: 'Pedidos' },
          { id: 'modelos', label: 'Modelos por tipo de visto' },
          { id: 'termo', label: 'Termo de consentimento' },
        ]}
      />

      {tab === 'pedidos' ? (
        <PedidosTab />
      ) : tab === 'termo' ? (
        <ConsentTermTab />
      ) : (
        <>
          <RetentionSetting />
          <ModelosTab />
        </>
      )}
      </div>
    </div>
  );
};

const PedidosTab: React.FC = () => {
  const { t } = useTranslation();
  const notifications = useNotifications();
  const [status, setStatus] = useState<'' | ChecklistStatus>('submitted');
  const [rows, setRows] = useState<AdminChecklist[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios
      .get<AdminChecklist[]>('/documentos-cliente', { params: status ? { status } : {} })
      .then((res) => setRows(res.data))
      .catch(() => notifications.error.network())
      .finally(() => setLoading(false));
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value || 'all'}
            type="button"
            onClick={() => setStatus(f.value)}
            className={`text-sm px-3 py-1.5 rounded-full border ${
              status === f.value ? 'bg-primary-600 border-primary-600 text-white' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500">Nenhum pedido neste filtro.</p>
      ) : (
        <ul className="bg-white rounded-lg shadow divide-y divide-gray-100">
          {rows.map((row) => (
            <li key={row._id}>
              <Link
                to={`/admin/clientes/${row.clienteId}`}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 px-4 py-3 hover:bg-gray-50"
              >
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 truncate">{row.clienteNome || 'Cliente'}</p>
                  <p className="text-xs text-gray-500">
                    {t(`visaForm.goals.${row.visaType}`)} · {STATUS_LABELS[row.status]}
                    {row.submittedAt && ` · enviado em ${new Date(row.submittedAt).toLocaleDateString('pt-BR')}`}
                  </p>
                </div>
                <span className="text-sm text-gray-700 tabular-nums">
                  {row.progress.uploaded}/{row.progress.total} enviados
                  {row.progress.missingRequired > 0 && (
                    <span className="text-amber-700"> · {row.progress.missingRequired} obrigatório(s) faltando</span>
                  )}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// Prazo padrão para eliminar os arquivos após a conclusão (cada pedido pode ter o próprio)
const RetentionSetting: React.FC = () => {
  const notifications = useNotifications();
  const [days, setDays] = useState<number | ''>('');
  const [saved, setSaved] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get<{ retentionDays: number }>('/documentos-cliente/settings')
      .then((res) => {
        setDays(res.data.retentionDays);
        setSaved(res.data.retentionDays);
      })
      .catch(() => notifications.error.network());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!days) return;
    setSaving(true);
    try {
      const res = await axios.put<{ retentionDays: number }>('/documentos-cliente/settings', { retentionDays: days });
      setSaved(res.data.retentionDays);
      notifications.showSuccess('Prazo salvo', 'Vale para os pedidos que usam o prazo padrão.');
    } catch (err) {
      notifications.showError('Erro', errorMessage(err, 'Não foi possível salvar o prazo.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-4 sm:p-5 mb-6 flex flex-col sm:flex-row sm:items-end gap-3">
      <label className="text-sm flex-1">
        <span className="block font-medium text-gray-900 mb-0.5">Prazo padrão de eliminação dos arquivos</span>
        <span className="block text-xs text-gray-600 mb-2">
          Dias após o pedido ser marcado como concluído. Aparece no termo de consentimento do cliente; cada pedido pode
          ter um prazo próprio.
        </span>
        <input
          type="number"
          min={1}
          max={365}
          value={days}
          onChange={(e) => setDays(e.target.value === '' ? '' : Math.min(365, Math.max(1, Number(e.target.value) || 1)))}
          className="field w-32"
        />
      </label>
      <Button size="sm" className="self-start sm:self-auto"
        type="button"
        onClick={save}
        disabled={saving || !days || days === saved}>
        <Save className="w-4 h-4" />
        {saving ? 'Salvando...' : 'Salvar prazo'}
      </Button>
    </div>
  );
};

interface ConsentTermText {
  title: string;
  body: string;
  accept: string;
}

interface ConsentTerm {
  _id: string;
  version: string | null;
  status: 'draft' | 'active' | 'archived';
  i18n: Record<Lang, ConsentTermText>;
  publishedAt?: string | null;
  publishedBy?: { name: string } | null;
  acceptances?: number;
}

interface ConsentTermsResponse {
  active: ConsentTerm | null;
  draft: ConsentTerm | null;
  versions: ConsentTerm[];
  preview: { days: number; company: string };
}

const LANG_NAMES: Record<Lang, string> = { pt: 'Português', en: 'English', es: 'Español' };

function toTriple(i18n?: Partial<Record<Lang, Partial<ConsentTermText>>>): I18nTriple {
  const pick = (l: Lang) => ({ title: i18n?.[l]?.title || '', body: i18n?.[l]?.body || '', accept: i18n?.[l]?.accept || '' });
  return { pt: pick('pt'), en: pick('en'), es: pick('es') };
}

/** Mesmo recorte do backend (config/documentConsent.js): parágrafos por linha em branco + marcadores. */
function renderTermPreview(i18n: I18nTriple, lang: Lang, days: number, company: string) {
  const block = (i18n[lang]?.body || '').trim() ? i18n[lang] : i18n.pt;
  const fill = (text = '') => text.replace(/\{\{\s*days\s*\}\}/g, String(days)).replace(/\{\{\s*company\s*\}\}/g, company);
  return {
    usesFallback: block !== i18n[lang],
    title: fill(block.title),
    paragraphs: (block.body || '')
      .split(/\n\s*\n/)
      .map((p) => p.replace(/\s*\n\s*/g, ' ').trim())
      .filter(Boolean)
      .map(fill),
    accept: fill(block.accept),
  };
}

const TermPreview: React.FC<{ i18n: I18nTriple; days: number; company: string }> = ({ i18n, days, company }) => {
  const [lang, setLang] = useState<Lang>('pt');
  const preview = renderTermPreview(i18n, lang, days, company);
  return (
    <div className="border border-gray-200 rounded-lg bg-gray-50 p-4">
      <div className="flex items-center justify-between gap-2 mb-3">
        <span className="text-xs font-medium uppercase tracking-wide text-gray-600">Prévia como o cliente vê</span>
        <Tabs variant="segmented" value={lang} onChange={setLang} items={LANG_TABS.map((l) => ({ id: l.key, label: l.label }))} />
      </div>
      {preview.usesFallback && (
        <p className="mb-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
          Sem texto em {LANG_NAMES[lang]}: o cliente verá a versão em português.
        </p>
      )}
      <h3 className="flex items-center gap-2 font-semibold text-gray-900 mb-2">
        <ShieldCheck className="w-4 h-4 text-primary-600" />
        {preview.title}
      </h3>
      <div className="space-y-2 text-sm text-gray-700 leading-relaxed">
        {preview.paragraphs.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>
      <p className="mt-3 text-sm text-gray-800 flex items-start gap-2">
        <input type="checkbox" disabled className="mt-0.5" />
        {preview.accept}
      </p>
    </div>
  );
};

const ConsentTermTab: React.FC = () => {
  const notifications = useNotifications();
  const confirm = useConfirm();
  const [data, setData] = useState<ConsentTermsResponse | null>(null);
  const [edit, setEdit] = useState<I18nTriple | null>(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [openVersion, setOpenVersion] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get<ConsentTermsResponse>('/documentos-cliente/consent-terms');
      setData(res.data);
      setEdit(res.data.draft ? toTriple(res.data.draft.i18n) : null);
      setDirty(false);
    } catch {
      notifications.error.network();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      notifications.showError('Erro', errorMessage(err, 'Não foi possível concluir a ação.'));
    } finally {
      setBusy(false);
    }
  };

  const startDraft = () =>
    run(async () => {
      await axios.put('/documentos-cliente/consent-terms/draft', {});
      await load();
    });

  const saveDraft = () =>
    run(async () => {
      await axios.put('/documentos-cliente/consent-terms/draft', { i18n: edit });
      setDirty(false);
      notifications.showSuccess('Rascunho salvo', 'Os clientes continuam vendo a versão publicada até você publicar.');
    });

  const discardDraft = async () => {
    const ok = await confirm({
      title: 'Descartar rascunho',
      message: 'As alterações do rascunho serão perdidas. A versão publicada não muda.',
      confirmText: 'Descartar',
      variant: 'danger',
    });
    if (!ok) return;
    run(async () => {
      await axios.delete('/documentos-cliente/consent-terms/draft');
      await load();
    });
  };

  const publish = async () => {
    const ok = await confirm({
      title: 'Publicar nova versão do termo',
      message:
        'A nova versão passa a valer para os próximos aceites. Os clientes que já aceitaram continuam vinculados à versão que leram, e as versões publicadas não podem mais ser editadas.',
      confirmText: 'Publicar',
    });
    if (!ok) return;
    run(async () => {
      await axios.put('/documentos-cliente/consent-terms/draft', { i18n: edit });
      const res = await axios.post<ConsentTerm>('/documentos-cliente/consent-terms/draft/publish');
      notifications.showSuccess('Termo publicado', `Versão ${res.data.version} em vigor.`);
      await load();
    });
  };

  if (!data) return <Loader2 className="w-6 h-6 animate-spin text-gray-400" />;

  const { active, versions, preview } = data;
  const missingLangs = edit ? (['en', 'es'] as Lang[]).filter((l) => !(edit[l]?.body || '').trim()) : [];

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Termo de consentimento</h2>
            <p className="text-sm text-gray-600">
              O cliente aceita este termo antes de enviar documentos. Cada aceite fica vinculado à versão lida; para mudar o
              texto, edite um rascunho e publique uma nova versão.
            </p>
          </div>
          {!edit && (
            <Button size="sm" className="self-start" type="button" onClick={startDraft} disabled={busy}>
              <Pencil className="w-4 h-4" />
              Editar termo
            </Button>
          )}
        </div>

        {active && (
          <p className="text-xs text-gray-600 mb-4">
            Em vigor: <strong>versão {active.version}</strong>
            {active.publishedAt && ` · publicada em ${new Date(active.publishedAt).toLocaleDateString('pt-BR')}`}
            {active.publishedBy?.name && ` por ${active.publishedBy.name}`} · {active.acceptances || 0} aceite(s)
          </p>
        )}

        {edit ? (
          <div className="grid gap-6 lg:grid-cols-2">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-amber-700 mb-2">Rascunho (não publicado)</p>
              <I18nTripleFields
                mode="documentConsent"
                value={edit}
                onChange={(next) => {
                  setEdit(next);
                  setDirty(true);
                }}
              />
              {missingLangs.length > 0 && (
                <p className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                  Sem texto em {missingLangs.map((l) => LANG_NAMES[l]).join(' e ')}: esses clientes verão o termo em português.
                  Use "Traduzir com IA" na aba do idioma.
                </p>
              )}
              <div className="flex flex-wrap gap-2 mt-4">
                <Button variant="ghost" size="sm" type="button" onClick={saveDraft} disabled={busy || !dirty}>
                  <Save className="w-4 h-4" />
                  Salvar rascunho
                </Button>
                <Button size="sm" type="button" onClick={publish} disabled={busy}>
                  <Upload className="w-4 h-4" />
                  Publicar nova versão
                </Button>
                <Button variant="ghost" size="sm" className="text-red-700 hover:bg-red-50 border-red-200" type="button" onClick={discardDraft} disabled={busy}>
                  <Trash2 className="w-4 h-4" />
                  Descartar
                </Button>
              </div>
            </div>
            <TermPreview i18n={edit} days={preview.days} company={preview.company} />
          </div>
        ) : (
          active && <TermPreview i18n={toTriple(active.i18n)} days={preview.days} company={preview.company} />
        )}
      </div>

      {versions.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <h3 className="font-semibold text-gray-900 mb-3">Versões publicadas</h3>
          <ul className="divide-y divide-gray-100">
            {versions.map((v) => {
              const open = openVersion === v._id;
              return (
                <li key={v._id} className="py-2">
                  <button type="button" onClick={() => setOpenVersion(open ? null : v._id)} className="w-full flex items-center justify-between gap-3 text-left text-sm">
                    <span>
                      <strong>{v.version}</strong>
                      {v.status === 'active' && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800">em vigor</span>}
                      <span className="text-gray-500">
                        {v.publishedAt && ` · ${new Date(v.publishedAt).toLocaleDateString('pt-BR')}`}
                        {v.publishedBy?.name && ` · ${v.publishedBy.name}`} · {v.acceptances || 0} aceite(s)
                      </span>
                    </span>
                    {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </button>
                  {open && (
                    <div className="mt-3">
                      <TermPreview i18n={toTriple(v.i18n)} days={preview.days} company={preview.company} />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

const LANG_TABS: { key: Lang; label: string }[] = [
  { key: 'pt', label: 'PT' },
  { key: 'en', label: 'EN' },
  { key: 'es', label: 'ES' },
];

const emptyItem = (): TemplateItem => ({
  key: '',
  sides: 'single',
  required: true,
  acceptPdf: true,
  i18n: {
    pt: { label: '', description: '' },
    en: { label: '', description: '' },
    es: { label: '', description: '' },
  },
});

const ModelosTab: React.FC = () => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const notifications = useNotifications();
  const [templates, setTemplates] = useState<ChecklistTemplate[]>([]);
  const [visaType, setVisaType] = useState<VisaType>('trabalho_subordinado');
  const [items, setItems] = useState<TemplateItem[]>([]);
  const [lang, setLang] = useState<Lang>('pt');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get<ChecklistTemplate[]>('/documentos-cliente/templates');
      setTemplates(res.data);
    } catch {
      notifications.error.network();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const tpl = templates.find((x) => x.visaType === visaType);
    setItems(tpl ? tpl.items.map((i) => ({ ...i, i18n: { ...emptyItem().i18n, ...i.i18n } })) : []);
    setDirty(false);
  }, [templates, visaType]);

  const update = (idx: number, patch: Partial<TemplateItem>) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
    setDirty(true);
  };

  const updateText = (idx: number, field: 'label' | 'description', value: string) => {
    setItems((prev) =>
      prev.map((it, i) =>
        i === idx ? { ...it, i18n: { ...it.i18n, [lang]: { ...it.i18n[lang], [field]: value } } } : it
      )
    );
    setDirty(true);
  };

  const move = (idx: number, dir: -1 | 1) => {
    setItems((prev) => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
    setDirty(true);
  };

  const selectType = async (v: VisaType) => {
    if (
      dirty &&
      !(await confirm({ title: 'Há alterações não salvas neste modelo. Descartar?', confirmText: 'Descartar', variant: 'warning' }))
    )
      return;
    setVisaType(v);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put<ChecklistTemplate>(`/documentos-cliente/templates/${visaType}`, { items });
      setTemplates((prev) => prev.map((x) => (x.visaType === visaType ? res.data : x)));
      notifications.showSuccess('Modelo salvo', 'Vale para os próximos pedidos; os já enviados não mudam.');
    } catch (err) {
      notifications.showError('Erro', errorMessage(err, 'Não foi possível salvar o modelo.'));
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    'field';

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
      <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible -mx-4 px-4 lg:mx-0 lg:px-0">
        {VISA_TYPES.map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => selectType(v)}
            className={`whitespace-nowrap text-left text-sm px-3 py-2 rounded-lg ${
              visaType === v ? 'bg-primary-50 text-primary-800 font-medium' : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            {t(`visaForm.goals.${v}`)}
            <span className="ml-1 text-xs text-gray-500">
              ({templates.find((x) => x.visaType === v)?.items.length ?? 0})
            </span>
          </button>
        ))}
      </nav>

      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h2 className="text-lg font-semibold text-gray-900">{t(`visaForm.goals.${visaType}`)}</h2>
          <div className="flex items-center gap-2">
            <Tabs variant="segmented" value={lang} onChange={setLang} items={LANG_TABS.map((l) => ({ id: l.key, label: l.label }))} />
            <Button size="sm" type="button" onClick={save} disabled={saving || !dirty}>
              <Save className="w-4 h-4" />
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </div>

        <p className="text-xs text-gray-500 mb-4">
          Alterações valem para os próximos pedidos. Textos em EN/ES vazios usam o português.
        </p>

        <ul className="space-y-3">
          {items.map((item, idx) => (
            <li key={idx} className="border border-gray-200 rounded-lg p-3">
              <div className="flex gap-2">
                <div className="flex-1 space-y-2">
                  <input
                    value={item.i18n[lang].label}
                    onChange={(e) => updateText(idx, 'label', e.target.value)}
                    placeholder={lang === 'pt' ? 'Nome do documento' : item.i18n.pt.label || 'Nome do documento'}
                    className={inputClass}
                  />
                  <input
                    value={item.i18n[lang].description}
                    onChange={(e) => updateText(idx, 'description', e.target.value)}
                    placeholder={lang === 'pt' ? 'Orientação para o cliente (opcional)' : item.i18n.pt.description || 'Orientação (opcional)'}
                    className={inputClass}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <IconButton action="moveUp" onClick={() => move(idx, -1)} disabled={idx === 0} />
                  <IconButton action="moveDown" onClick={() => move(idx, 1)} disabled={idx === items.length - 1} />
                  <IconButton
                    action="delete"
                    label="Remover item"
                    onClick={() => {
                      setItems((prev) => prev.filter((_, i) => i !== idx));
                      setDirty(true);
                    }}
                  />
                </div>
              </div>
              <div className="flex flex-wrap gap-x-5 gap-y-2 mt-2 text-sm text-gray-700">
                <label className="flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={item.sides === 'front_back'}
                    onChange={(e) => update(idx, { sides: e.target.checked ? 'front_back' : 'single' })}
                  />
                  Frente e verso
                </label>
                <label className="flex items-center gap-1.5">
                  <input type="checkbox" checked={item.required} onChange={(e) => update(idx, { required: e.target.checked })} />
                  Obrigatório
                </label>
                <label className="flex items-center gap-1.5">
                  <input type="checkbox" checked={item.acceptPdf} onChange={(e) => update(idx, { acceptPdf: e.target.checked })} />
                  Aceita PDF
                </label>
              </div>
            </li>
          ))}
        </ul>

        <Button variant="ghost" size="sm" className="mt-4"
          type="button"
          onClick={() => {
            setItems((prev) => [...prev, emptyItem()]);
            setLang('pt');
            setDirty(true);
          }}>
          <Plus className="w-4 h-4" />
          Adicionar documento
        </Button>
       </div>
    </div>
  );
};


export default AdminDocumentos;
